import { canAll, CAP } from '@/lib/can'
import type { CounterpartyCategory, CounterpartyRow, CurrentUser } from '@/types/domain'

/* The counterparty section's rules that are not markup (S-22b): the category
 * vocabulary, the list page's client-side narrowing, the list read's query
 * string and the reader's rights. A module rather than a component file, so
 * the pages, the forms and the three pickers share one copy of each — and so
 * a non-component export does not break fast refresh for a component. */

/** The six categories, in the order the chips, the form and the docs list them.
 *  The same set lives in Go (`directory.Categories`) and in SQL (0077's domain);
 *  none of the three imports another. */
export const COUNTERPARTY_CATEGORIES: readonly CounterpartyCategory[] = [
  'carrier',
  'forwarder',
  'customs-broker',
  'driver',
  'agent',
  'other',
]

/** What may move a leg or a truck: `CarrierSelect`'s list and its create form.
 *  «Перевозчик» stays the role word on a leg; the company in it can be a
 *  haulier, a forwarder that sub-contracted the truck, or an owner-driver. */
export const CARRIER_CATEGORIES: readonly CounterpartyCategory[] = ['carrier', 'forwarder', 'driver']

export function isCounterpartyCategory(value: string): value is CounterpartyCategory {
  return (COUNTERPARTY_CATEGORIES as readonly string[]).includes(value)
}

/** The list page's narrowing: one chip (or «Все»), the search, and the
 *  «Неактивные» box. */
export interface CounterpartyListFilter {
  category: CounterpartyCategory | 'all'
  search: string
  /** Ticked, the list shows the deactivated rows TOGETHER WITH the active ones
   *  (owner, 2026-10-20); a deactivated row carries its «Неактивен» mark. */
  inactive: boolean
}

/** The rows the table shows. Narrowing only: the server already decided which
 *  rows this reader may see (an empty list without `counterparty.read`). */
export function visibleCounterparties(
  rows: readonly CounterpartyRow[],
  { category, search, inactive }: CounterpartyListFilter,
): CounterpartyRow[] {
  const needle = search.trim().toLocaleLowerCase()
  return rows.filter(
    (row) =>
      (row.isActive || inactive) &&
      (category === 'all' || row.category === category) &&
      (needle === '' || row.name.toLocaleLowerCase().includes(needle)),
  )
}

/** Per chip, how many rows it would show under the «Неактивные» box.
 *  The search does not move the counts: a chip says how many of that kind the
 *  company has, and a count that shrank with every keystroke would read as
 *  rows disappearing. */
export function categoryCounts(
  rows: readonly CounterpartyRow[],
  inactive: boolean,
): Record<CounterpartyCategory | 'all', number> {
  const counts = Object.fromEntries(
    [['all', 0], ...COUNTERPARTY_CATEGORIES.map((category) => [category, 0])],
  ) as Record<CounterpartyCategory | 'all', number>
  for (const row of rows) {
    if (!row.isActive && !inactive) continue
    counts.all += 1
    counts[row.category] += 1
  }
  return counts
}

/** The list read's filters, as `GET /api/counterparties` takes them. */
export interface CounterpartyQuery {
  /** Repeatable on the wire (`?category=carrier&category=forwarder`). Absent
   *  or empty asks for every category. */
  categories?: readonly CounterpartyCategory[]
  search?: string
  /** The section asks for every row once and filters on the client; a picker
   *  leaves this off and gets the active ones. */
  includeInactive?: boolean
}

/** The list read's path with its query string.
 *
 *  Built here rather than through `api.get`'s `params`, which take one value
 *  per key: `category` is repeatable, and a comma-joined value would be one
 *  unknown category to the server (422 `category: invalid`). */
export function counterpartiesPath({ categories, search, includeInactive }: CounterpartyQuery): string {
  const query = new URLSearchParams()
  for (const category of categories ?? []) query.append('category', category)
  if (search?.trim()) query.set('search', search.trim())
  if (includeInactive) query.set('includeInactive', 'true')
  const qs = query.toString()
  return qs ? `/counterparties?${qs}` : '/counterparties'
}

/** What this reader may do with counterparties — the server's rule, asked the
 *  server's way: READ beside each act, so `counterparty.create` or
 *  `counterparty.manage` alone (a migrated per-user grant on a Sales account)
 *  reaches nothing. An affordance only; the server refuses regardless. */
export interface CounterpartyRights {
  /** The section, the record page and the pickers' lists. */
  read: boolean
  /** Create, edit (the category included) and (de)activate — any record. */
  edit: boolean
  /** Delete and merge. Seeded to nobody; the superuser passes by flag. */
  manage: boolean
}

export function counterpartyRights(user: CurrentUser | null | undefined): CounterpartyRights {
  return {
    read: canAll(user, CAP.counterpartyRead),
    edit: canAll(user, CAP.counterpartyRead, CAP.counterpartyCreate),
    manage: canAll(user, CAP.counterpartyRead, CAP.counterpartyManage),
  }
}
